import {
  FIT_LABELS,
  FORMALITY_LABELS,
  PALETTE_LABELS,
  PATTERN_LABELS,
  VIBE_LABELS,
  formatBrands,
  type Profile,
} from '../shared/profile';

// The stored brief, read back as a standing column: label left, answer right,
// hairline between. Nothing here is editable in place — one Edit button
// reopens the quiz, so there is exactly one way to change the brief.
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-rule py-2.5">
      <span className="kicker shrink-0">{label}</span>
      <span className="text-right text-sm text-ink">{value}</span>
    </div>
  );
}

function sizeLine(profile: Profile): string {
  const { neck, chest, waist } = profile.sizes;
  const parts = [`Chest ${chest}`];
  if (neck != null) parts.push(`neck ${neck}"`);
  if (waist != null) parts.push(`waist ${waist}"`);
  return parts.join(' · ');
}

export function ProfilePane({
  profile,
  updatedAt,
  onEdit,
}: {
  profile: Profile;
  updatedAt?: string;
  onEdit: () => void;
}) {
  const formality =
    profile.formality.min === profile.formality.max
      ? FORMALITY_LABELS[profile.formality.min]
      : `${FORMALITY_LABELS[profile.formality.min]} → ${FORMALITY_LABELS[profile.formality.max]}`;

  const { valueMaxUsd, midMaxUsd, midOnlyIfDiscountPct } = profile.budget;

  return (
    <section className="px-5 pb-10">
      <div className="mx-auto max-w-2xl">
        <p className="kicker">The brief</p>
        <h2 className="display-sm mt-2 text-2xl">On file</h2>

        <div className="mt-5">
          <Row label="Sizes" value={sizeLine(profile)} />
          <Row label="Fit" value={FIT_LABELS[profile.fit]} />
          <Row label="Palette" value={PALETTE_LABELS[profile.palette]} />
          <Row label="Patterns" value={PATTERN_LABELS[profile.patternTolerance]} />
          <Row label="Formality" value={formality} />
          <Row
            label="Budget"
            value={`Value under $${valueMaxUsd} · mid to $${midMaxUsd} at ${midOnlyIfDiscountPct}%+ off`}
          />
          <Row label="Brands" value={profile.brands.length ? formatBrands(profile.brands) : '—'} />
          <Row
            label="Vibes"
            value={
              profile.vibes.length ? profile.vibes.map((v) => VIBE_LABELS[v]).join(' · ') : '—'
            }
          />
        </div>

        <div className="mt-6 flex items-center gap-4">
          <button
            type="button"
            onClick={onEdit}
            className="rounded-xs border border-ink px-4 py-2 text-sm text-ink transition-colors hover:bg-ink hover:text-paper"
          >
            Edit the brief
          </button>
          {updatedAt && (
            <span className="font-mono text-[0.6875rem] text-ink-faint tabular">
              filed {new Date(updatedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
